import React from 'react';
import { Card, Row, Col, Statistic, Space, Table, Empty, Tag, Text, Tooltip } from 'antd';
import { DollarOutlined, NumberOutlined, PercentageOutlined } from '@ant-design/icons';

/**
 * 机场费用统计面板组件（通用）
 * 用于 Ground Handling、Landing & Parking、Navigation 等费用类型
 */
const AirportFeesPanel = ({ 
    data, 
    title, 
    icon, 
    color = '#13c2c2',
    countLabel = '次数',
    tooltipText = '费用占总成本的比例'
}) => {
    if (!data || !data.overview) {
        return <Empty description={`暂无${title}数据`} />;
    }

    return (
        <div style={{ padding: '16px' }}>
            {/* 总览卡片 */}
            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                <Col xs={24} sm={8}>
                    <Card hoverable style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
                        <Statistic
                            title={
                                <Space>
                                    <DollarOutlined style={{ color }} />
                                    <span>平均费用</span>
                                </Space>
                            }
                            value={data.overview.avg_cost}
                            precision={2}
                            valueStyle={{ color, fontSize: '24px' }}
                            prefix="$"
                            suffix="USD"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card hoverable style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
                        <Statistic
                            title={
                                <Space>
                                    <NumberOutlined style={{ color: '#52c41a' }} />
                                    <span>{countLabel}</span>
                                </Space>
                            }
                            value={data.overview.count}
                            valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                            suffix="次"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card hoverable style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
                        <Tooltip title={tooltipText}>
                            <Statistic
                                title={
                                    <Space>
                                        <PercentageOutlined style={{ color: '#faad14' }} />
                                        <span>占总成本比例</span>
                                    </Space>
                                }
                                value={data.overview.percentage}
                                precision={2}
                                valueStyle={{ color: '#faad14', fontSize: '24px' }}
                                suffix="%"
                            />
                        </Tooltip>
                    </Card>
                </Col>
            </Row>

            {/* 按机场和飞机排行 */}
            <Row gutter={[16, 16]}>
                {/* 机场排行榜 */}
                <Col xs={24} lg={12}>
                    <Card 
                        title={
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                <span style={{ marginRight: '4px' }}>🏢</span>
                                机场排行榜 (Top 10 按平均值)
                            </span>
                        }
                        size="small"
                        style={{ borderRadius: '8px', boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)' }}
                    >
                        {data.by_airport && data.by_airport.length > 0 ? (
                            <Table
                                dataSource={data.by_airport}
                                rowKey="airport_code"
                                pagination={false}
                                size="small"
                                bordered
                                style={{ borderRadius: '4px', overflow: 'hidden' }}
                                columns={[
                                    {
                                        title: '排名',
                                        key: 'rank',
                                        width: 80,
                                        align: 'center',
                                        render: (_, __, index) => (
                                            <Tag color={index === 0 ? 'red' : index === 1 ? 'orange' : index === 2 ? 'gold' : 'default'}>
                                                {index + 1}
                                            </Tag>
                                        )
                                    },
                                    {
                                        title: '机场代码',
                                        dataIndex: 'airport_code',
                                        key: 'airport_code',
                                        width: 120,
                                        align: 'center',
                                        render: (code) => <span style={{ fontWeight: 600, fontSize: '14px' }}>{code}</span>
                                    },
                                    {
                                        title: countLabel,
                                        dataIndex: 'count',
                                        key: 'count',
                                        width: 100,
                                        align: 'center',
                                        render: (count) => <span>{count} 次</span>
                                    },
                                    {
                                        title: '平均费用 (USD)',
                                        dataIndex: 'avg_cost',
                                        key: 'avg_cost',
                                        align: 'right',
                                        render: (cost) => (
                                            <span style={{ fontWeight: 600, color, fontSize: '14px' }}>
                                                ${cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                            </span>
                                        )
                                    }
                                ]}
                            />
                        ) : (
                            <Empty description="暂无机场数据" />
                        )}
                    </Card>
                </Col>

                {/* 飞机/机型对比 */}
                <Col xs={24} lg={12}>
                    <Card 
                        title={
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                <span style={{ marginRight: '4px' }}>✈️</span>
                                机型/飞机对比
                            </span>
                        }
                        size="small"
                        style={{ borderRadius: '8px', boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)' }}
                    >
                        {data.by_aircraft && data.by_aircraft.length > 0 ? (
                            <Table
                                dataSource={data.by_aircraft}
                                rowKey="aircraft_reg"
                                pagination={false}
                                size="small"
                                bordered
                                style={{ borderRadius: '4px', overflow: 'hidden' }}
                                columns={[
                                    {
                                        title: '飞机注册号',
                                        dataIndex: 'aircraft_reg',
                                        key: 'aircraft_reg',
                                        width: 120,
                                        align: 'center',
                                        render: (reg) => <span style={{ fontWeight: 600, fontSize: '14px', color: '#1890ff' }}>{reg}</span>
                                    },
                                    {
                                        title: '机型',
                                        dataIndex: 'aircraft_type',
                                        key: 'aircraft_type',
                                        width: 100,
                                        align: 'center',
                                        render: (type) => <span style={{ fontSize: '13px' }}>{type || '-'}</span>
                                    },
                                    {
                                        title: countLabel,
                                        dataIndex: 'count',
                                        key: 'count',
                                        width: 100,
                                        align: 'center',
                                        render: (count) => <span>{count} 次</span>
                                    },
                                    {
                                        title: '平均费用 (USD)',
                                        dataIndex: 'avg_cost',
                                        key: 'avg_cost',
                                        align: 'right',
                                        render: (cost) => (
                                            <span style={{ fontWeight: 600, color, fontSize: '14px' }}>
                                                ${cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                            </span>
                                        )
                                    }
                                ]}
                            />
                        ) : (
                            <Empty description="暂无飞机数据" />
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default AirportFeesPanel;
